import { ImageInfo } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Image';
import { NewRegistrationItem } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ConfigurationStorage/NewRegistrationItem';
import { DeclaredInterfaceDto } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ConfigurationStorage/DeclaredInterfaceDto';
import { NewRegistrationFactory } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ConfigurationStorage/NewRegistrationFactory';
import {
  createNewRegistration,
  getNewRegistrationCategories,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ConfigurationStorage/ConfigurationStorageService';
import { getElementTypeIcon, getImageInfoUrl, getStoredImageUrl } from 'utils/ImageUtils';
import { IDesignPatternTemplate, IRegistrationTemplate } from './TemplateListItem';
import { ElementType } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ConfigurationStorage/ElementType';
import { IChoiceItem } from 'components/Dialogs/EitherOrDialog/ChoiceItem';
import { RunBaseActionCallback } from './CreateRegistrationDialog';
import { folderTypeName } from 'components/EntityPicker/EpickUtils';
import { NewRegistrationResult } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ConfigurationStorage/NewRegistrationResult';

/**
 * Primary actions (Create Flow, Create Rule, ...) of designer folder for showing "Create Registration Dialog"
 */
export type ActionKey = 'Rule' | 'Flow' | 'Form' | 'Report' | 'DataTypes/Integration';

export function getImageUrl(icon: ImageInfo): string | null | undefined {
  if (icon == null) {
    return null;
  }

  const iconColor = '#0081C2';
  const iconSize = 32;

  const iconUrl = getImageInfoUrl(icon, iconSize, iconSize, iconColor);

  return iconUrl;
}

export function convertToTemplate(item: NewRegistrationItem): IRegistrationTemplate {
  return {
    id: item.Id,
    imageUrl: getImageUrl(item.Icon),
    name: item.Name,

    order: item.Order,
    showBadge: item.Kind === 'template',
    badgeText: 'template',
    categoryName: item.CategoryName,
    factoryName: item.FactoryName,
    description: item.Description,
    favoriteType: item.FavoriteType,
    registrationItem: item,
  };
}

export const primaryCategory = 'Primary';
export const secondaryCategory = 'Secondary';

export async function loadCategories(folderId: string, actionKey: string): Promise<NewRegistrationFactory[]> {
  const categories = await getNewRegistrationCategories({
    folderId,
    actionKey,
  });

  return categories
    .filter(category => category.Name !== primaryCategory && category.Name !== secondaryCategory)
    .sort((item1, item2) => item1.Order - item2.Order);
}

export interface ICreateRegistrationOptions {
  actionKey: ActionKey;
  folderId: string;
  template: NewRegistrationItem;
  name: string;
  handleCreatedRegistration: (newRegistration: NewRegistrationResult) => void;
}

export function runRegistrationBaseAction(
  folderId: string,
  template: NewRegistrationItem,
  runBaseAction: RunBaseActionCallback
) {
  if (template.Action) {
    runBaseAction(folderId, template.Action, folderTypeName);
  }
}

export async function createRegistration({
  actionKey,
  folderId,
  handleCreatedRegistration,
  name,
  template,
}: ICreateRegistrationOptions) {
  !IS_TEST && $Portal.Progress.showLoadingIndicator();

  try {
    const registration = await createNewRegistration({
      folderId,
      actionKey,
      categoryKey: template.CategoryName,
      itemId: template.Id,
      providedName: name,
    });
    handleCreatedRegistration(registration);
  } finally {
    !IS_TEST && $Portal.Progress.hideLoadingIndicator();
  }
}

/**
 *  Filters design patterns and sort them to put default patterns before the rest.
 */
export function convertDesignPatternToTemplates(patterns: DeclaredInterfaceDto[]): IDesignPatternTemplate[] {
  return patterns.map(item => ({
    id: item.Id,
    description: item.Description,
    imageUrl: getImageUrl(item.Image),
    name: item.Name,
    factoryName: '',
    categoryName: '',
    showBadge: item.IsDefaultForFolder,
    badgeText: 'default',
    // default design patterns should be in the beginning
    order: item.IsDefaultForFolder ? 0 : 1,
    pattern: item,
  }));
}

export function getEntityIconUrl(elementType: keyof typeof ElementType): string {
  return getElementTypeIcon(ElementType[elementType], 80, 80, '#0081C2');
}

export function getDesignPatternIconUrl(): string {
  return getStoredImageUrl('behavior icons|design-pattern.svg', 80, 80, '#0081C2') ?? '';
}

export function getDefaultDesignPatternIconUrl(): string {
  return getStoredImageUrl('behavior icons|design-pattern-default.svg', 80, 80, '#0081C2') ?? '';
}

//TODO alex: need to get these types from server side
export function getElementTypes(actionKey?: ActionKey): ElementType[] {
  if (actionKey === 'Flow') {
    return [ElementType.Flow, ElementType.SimpleFlow];
  } else if (actionKey === 'Rule') {
    return [ElementType.Rule, ElementType.MatrixRule, ElementType.TruthTable, ElementType.RuleTable];
  } else if (actionKey === 'Form') {
    return [ElementType.Form];
  }

  return [];
}

export function areAllPatternsDefault(patterns?: DeclaredInterfaceDto[]) {
  return patterns != null && patterns.length !== 0 && !patterns.every(item => item.IsDefaultForFolder);
}

/**
 * Determines can be entity create with using design pattern.
 *
 * @param actionKey Primary actions (Create Flow, Create Rule, ...) of designer folder for showing "Create Registration Dialog"
 */
export function supportsDesignPattern(actionKey: ActionKey) {
  return actionKey === 'Flow' || actionKey === 'Rule' || actionKey === 'Form';
}

export const useDefaultPatternId = 'use-default-pattern';
export const pickDesignPatternId = 'pick-design-pattern';

export function createUseDefaultPatternChoice(patterns: DeclaredInterfaceDto[], onClose: () => void): IChoiceItem {
  const title = 'Use Default Design Pattern';

  return {
    id: useDefaultPatternId,
    title: title,
    asButtonText: title,
    illustration: { url: getDefaultDesignPatternIconUrl() },
    buttonText: 'Use Default Design Pattern',
    dialogOptions: {
      height: 800,
      width: 800,
      minHeight: 500,
      minWidth: 550,
      resizable: true,
    },
  };
}

export function createPickPatternChoice(onClose: () => void): IChoiceItem {
  return {
    id: 'pick-design-pattern',
    title: 'Use Design Pattern',
    illustration: { url: getDesignPatternIconUrl() },
    buttonText: 'Use Design Pattern',
    dialogOptions: {
      height: 800,
      width: 800,
      minHeight: 500,
      minWidth: 550,
      resizable: true,
    },
  };
}

export const scratchChoiceId = 'scratch';
export const scratchChoiceName = 'Create New';

export function createScratchChoice(actionKey: ActionKey, onClose: () => void) {
  // Either or dialog is shown only for entity which supports design pattern,
  // otherwise we don't need to show illustration as simple dialog will be shown
  const illustrationUrl = supportsDesignPattern(actionKey)
    ? getEntityIconUrl(actionKey as keyof typeof ElementType)
    : '';

  return {
    id: scratchChoiceId,
    title: 'Create New',
    illustration: { url: illustrationUrl },
    buttonText: 'Create',
    dialogOptions: {
      height: 800,
      minHeight: 600,
      width: 1200,
      minWidth: 875,
      resizable: true,
    },
  };
}
